import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, TextField, IconButton } from "@mui/material";
import {
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from "@mui/icons-material";

interface EditableFieldProps {
  label: string;
  value: string;
  placeholder?: string;
  emptyText?: string;
  canEdit: boolean;
  isUpdating: boolean;
  onSave: (value: string) => Promise<void>;
  formatDisplay?: (value: string) => string;
  testIdPrefix: string;
  fontWeight?: number;
  variant?: "body1" | "body2";
}

const EditableField: React.FC<EditableFieldProps> = ({
  label,
  value,
  placeholder = `Enter ${label.toLowerCase()}`,
  emptyText = `No ${label.toLowerCase()} set`,
  canEdit,
  isUpdating,
  onSave,
  formatDisplay,
  testIdPrefix,
  fontWeight = 500,
  variant = "body2",
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editingValue, setEditingValue] = useState(value);

  // Sync editing value when entering edit mode or value changes
  useEffect(() => {
    if (isEditing) {
      setEditingValue(value);
    }
  }, [isEditing, value]);

  const handleSave = useCallback(async () => {
    if (editingValue.trim() !== value) {
      await onSave(editingValue.trim());
    }
    setIsEditing(false);
  }, [editingValue, value, onSave]);

  const handleCancel = useCallback(() => {
    setIsEditing(false);
  }, []);

  const displayValue = value && value.trim() !== ""
    ? (formatDisplay ? formatDisplay(value) : value)
    : emptyText;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 0.5,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        {canEdit && !isEditing && (
          <IconButton
            size="small"
            onClick={() => setIsEditing(true)}
            sx={{ color: "text.secondary", p: 0.5 }}
            data-testid={`profile-edit-${testIdPrefix}-button`}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      {isEditing ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <TextField
            value={editingValue}
            onChange={(e) => setEditingValue(e.target.value)}
            size="small"
            placeholder={placeholder}
            fullWidth
            autoFocus
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleSave();
              }
            }}
            data-testid={`profile-${testIdPrefix}-input`}
          />
          <IconButton
            size="small"
            onClick={handleSave}
            disabled={isUpdating}
            sx={{ color: "success.main" }}
            data-testid={`profile-save-${testIdPrefix}-button`}
          >
            <CheckIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={handleCancel}
            sx={{ color: "error.main" }}
            data-testid={`profile-cancel-${testIdPrefix}-button`}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      ) : (
        <Typography variant={variant} fontWeight={fontWeight}>
          {displayValue}
        </Typography>
      )}
    </Box>
  );
};

export default EditableField;
